import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  FileText, Loader2, Send, Copy, ShieldCheck, Link as LinkIcon, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const PUBLIC_FRONTEND = process.env.REACT_APP_BACKEND_URL;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function RomaneioModal({ ticketId, open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);

  const carregar = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/tickets-ops/${ticketId}/romaneio`);
      setData(d);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { if (open) { carregar(); setLinkInfo(null); } }, [open, carregar]);

  const gerar = async () => {
    setGerando(true);
    try {
      await api.post(`/tickets-ops/${ticketId}/romaneio`);
      toast.success("Romaneio gerado");
      carregar();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setGerando(false); }
  };

  const gerarLink = async () => {
    try {
      const { data: d } = await api.post(`/tickets-ops/${ticketId}/romaneio/link-assinatura`);
      const url = `${PUBLIC_FRONTEND}/sign/${d.token}`;
      setLinkInfo({ ...d, url });
      toast.success("Link gerado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const copiar = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado!"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="modal-romaneio">
        <DialogHeader>
          <DialogTitle>Romaneio · Ticket #{ticketId?.slice(0, 8)}</DialogTitle>
          <DialogDescription>Geração de PDF e link para assinatura digital.</DialogDescription>
        </DialogHeader>

        {loading && <div className="text-center py-4"><Loader2 className="animate-spin inline" /></div>}

        {!loading && (!data || !data.existe) && (
          <div className="space-y-3 py-4 text-center">
            <FileText size={48} className="mx-auto text-slate-400" />
            <p className="text-sm text-slate-600">Nenhum romaneio gerado para este ticket.</p>
            <Button
              className="bg-orange-600 hover:bg-orange-700" onClick={gerar} disabled={gerando}
              data-testid="rom-gerar"
            >
              {gerando && <Loader2 size={12} className="animate-spin mr-2" />}
              <FileText size={14} className="mr-1" /> Gerar Romaneio PDF
            </Button>
          </div>
        )}

        {!loading && data?.existe && (
          <div className="space-y-3">
            <Card className="p-3 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">Status</span>
                {data.assinado ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                    <ShieldCheck size={10} className="mr-1" /> ASSINADO
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300">PENDENTE ASSINATURA</Badge>
                )}
              </div>
              <div className="text-slate-500">Gerado por {data.gerado_por_nome} em {new Date(data.gerado_em).toLocaleString("pt-BR")}</div>
              {data.assinado && (
                <div className="mt-2 pt-2 border-t text-emerald-700">
                  Assinado por <strong>{data.assinado_nome}</strong> em {new Date(data.assinado_em).toLocaleString("pt-BR")}
                  {data.protocolo && <div className="font-mono text-[10px] text-slate-500">Protocolo: {data.protocolo}</div>}
                </div>
              )}
            </Card>

            <div className="flex gap-2">
              <a
                href={`${API_BASE}${data.pdf_url}`}
                target="_blank" rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-slate-300 rounded text-xs font-medium hover:bg-slate-50"
                data-testid="rom-baixar-pdf"
              >
                <FileText size={12} className="mr-1" /> Abrir PDF
              </a>
              {!data.assinado && (
                <Button
                  variant="outline" onClick={gerar} disabled={gerando}
                  data-testid="rom-regenerar"
                  className="text-xs"
                >
                  <RefreshCw size={12} className="mr-1" /> Regenerar
                </Button>
              )}
            </div>

            {!data.assinado && (
              <Card className="p-3 bg-orange-50 border-orange-200 space-y-2">
                <div className="text-xs font-semibold text-orange-800 flex items-center gap-1">
                  <LinkIcon size={12} /> Link público de assinatura
                </div>
                {!linkInfo && (
                  <Button
                    size="sm" className="w-full bg-orange-600 hover:bg-orange-700"
                    onClick={gerarLink}
                    data-testid="rom-gerar-link"
                  >
                    <Send size={12} className="mr-1" /> Gerar link para o cliente
                  </Button>
                )}
                {linkInfo && (
                  <div className="space-y-2">
                    <div className="flex gap-1">
                      <Input value={linkInfo.url} readOnly className="text-xs font-mono h-8" data-testid="rom-link-url" />
                      <Button size="sm" variant="outline" onClick={() => copiar(linkInfo.url)} data-testid="rom-link-copiar">
                        <Copy size={12} />
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      Válido até {new Date(linkInfo.expira_em).toLocaleString("pt-BR")}. Envie via WhatsApp/SMS/e-mail ao cliente.
                    </p>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RomaneiosDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/tickets-ops/romaneios/dashboard");
      setData(d);
    } catch (e) { toast.error("Falha"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) return <Card className="p-8 text-center"><Loader2 className="animate-spin inline" /></Card>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="rom-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-l-4 border-blue-500" data-testid="kpi-rom-total">
          <div className="text-xs uppercase text-slate-500">Total</div>
          <div className="text-4xl font-bold text-blue-700 mt-1">{data.total}</div>
        </Card>
        <Card className="p-4 border-l-4 border-emerald-500" data-testid="kpi-rom-assinados">
          <div className="text-xs uppercase text-slate-500">Assinados</div>
          <div className="text-4xl font-bold text-emerald-700 mt-1">{data.assinados}</div>
        </Card>
        <Card className="p-4 border-l-4 border-amber-500" data-testid="kpi-rom-pendentes">
          <div className="text-xs uppercase text-slate-500">Pendentes</div>
          <div className="text-4xl font-bold text-amber-700 mt-1">{data.pendentes}</div>
        </Card>
        <Card className="p-4 border-l-4 border-orange-500" data-testid="kpi-rom-tempo">
          <div className="text-xs uppercase text-slate-500">Tempo médio</div>
          <div className="text-4xl font-bold text-orange-700 mt-1">{data.tempo_medio_assinatura_horas}<span className="text-lg">h</span></div>
          <div className="text-[10px] text-slate-500 mt-1">Geração → assinatura</div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="text-xs font-semibold text-slate-600 mb-2">Taxa de assinatura: <strong className="text-orange-700">{data.taxa_assinatura_pct}%</strong></div>
        <div className="bg-slate-200 h-2 rounded overflow-hidden">
          <div className="bg-emerald-500 h-full" style={{ width: `${data.taxa_assinatura_pct}%` }} />
        </div>
      </Card>

      <Card>
        <div className="p-3 border-b">
          <h4 className="text-xs uppercase text-slate-600 font-semibold">Romaneios recentes</h4>
        </div>
        <div className="divide-y">
          {(data.recentes || []).map((r) => (
            <div key={r.id} className="p-3 text-xs flex items-center justify-between" data-testid={`rom-recente-${r.id}`}>
              <div>
                <div className="font-mono">#{r.ticket_id.slice(0, 8)}</div>
                <div className="text-slate-500">{r.cliente_nome}</div>
              </div>
              <div className="text-right">
                {r.assinado ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">
                    <ShieldCheck size={10} className="mr-1" /> ASSINADO
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">PENDENTE</Badge>
                )}
                <div className="text-[10px] text-slate-500 mt-1">
                  {r.assinado ? `por ${r.assinado_nome}` : `gerado por ${r.gerado_por_nome}`}
                </div>
              </div>
            </div>
          ))}
          {(data.recentes || []).length === 0 && (
            <div className="p-6 text-center text-xs text-slate-400">Nenhum romaneio gerado ainda.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
